import { ReadonlyURLSearchParams } from 'next/navigation'
import { FilterProperty, SortOption } from '../types/collection.types'

export const subtypeFilterProperty: FilterProperty = {
  title: 'Subtype',
  id: 'subtype',
  groqAllOptionsKey: 'subtypeOptions',
  groqFilterKey: 'subtypes',
  dictionaryKey: 'subtype',
}

export const brandFilterProperty: FilterProperty = {
  title: 'Brand',
  id: 'brand',
  groqAllOptionsKey: 'brandOptions',
  groqFilterKey: 'brands',
  dictionaryKey: 'brand',
}

export const colorFilterProperty: FilterProperty = {
  title: 'Color',
  id: 'color',
  groqAllOptionsKey: 'colorFamilyOptions',
  groqFilterKey: 'colorFamilies',
  dictionaryKey: 'color',
}

export const styleFilterProperty: FilterProperty = {
  title: 'Style',
  id: 'style',
  groqAllOptionsKey: 'styleOptions',
  groqFilterKey: 'styles',
  dictionaryKey: 'style',
}

export const sizeFilterProperty: FilterProperty = {
  title: 'Size',
  id: 'size',
  groqAllOptionsKey: 'availableSizesOptions',
  groqAllOptionsMapper: (input: string) => ({ _id: input, _type: 'availableSize', urlSlug: input, title: input }),
  groqFilterKey: 'availableSizes',
  dictionaryKey: 'size',
}

export const filterProperties: FilterProperty[] = [subtypeFilterProperty, brandFilterProperty, styleFilterProperty, colorFilterProperty, sizeFilterProperty]

export const defaultSortOption: SortOption = { title: 'Manual', id: 'relevance', groq: null, dictionaryKey: 'miistaspicks', isDefault: true }
export const latestDescSortOption: SortOption = { title: 'New in', id: 'latest-desc', groq: 'order(_createdAt desc, _updatedAt desc)', dictionaryKey: 'newin' }
export const priceAscSortOption: SortOption = { title: 'Price Asc', id: 'price-asc', groq: 'order(maxVariantPrice asc, _createdAt desc)', dictionaryKey: 'pricelowtohigh' }
export const priceDescSortOption: SortOption = { title: 'Price Desc', id: 'price-desc', groq: 'order(maxVariantPrice desc, _createdAt desc)', dictionaryKey: 'pricehightolow' }
export const sortOptions: SortOption[] = [defaultSortOption, latestDescSortOption, priceAscSortOption, priceDescSortOption]

/**
 * Get the sort option from URL search parameters.
 * Defaults to the default sort option if no match is found.
 */
export const currentSortOption = (pageSearchParams: ReadonlyURLSearchParams | URLSearchParams): SortOption => {
  if (!pageSearchParams) {
    return defaultSortOption
  }

  const searchPageSearchParam = pageSearchParams.get('sort')

  return sortOptions.find((sortOption) => sortOption.id === searchPageSearchParam) || defaultSortOption
}

/**
 * Get filter properties and their values from URL search parameters.
 */
export const currentUrlParamRefinements = (pageSearchParams: ReadonlyURLSearchParams | URLSearchParams): { property: FilterProperty; values: string[] }[] => {
  if (!pageSearchParams) {
    return []
  }

  return filterProperties.reduce(
    (acc, property) => {
      const values = pageSearchParams.getAll(property.id)

      if (values.length > 0) {
        acc.push({ property, values })
      }

      return acc
    },
    [] as { property: FilterProperty; values: string[] }[],
  )
}

/**
 * Convert a plain object to URLSearchParams.
 */
export const convertToUrlSearchParams = (pageSearchParams?: Record<string, string | string[] | undefined>): URLSearchParams => {
  const params = new URLSearchParams()

  if (!pageSearchParams) {
    return params
  }

  Object.entries(pageSearchParams).forEach(([key, value]) => {
    if (value === undefined) {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v))
    } else {
      params.append(key, value)
    }
  })

  return params
}
